import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useSeo } from "../lib/seo";
import { IMAGES, PHONE, PHONE_HREF, EMAIL } from "../lib/constants";
import { PageHero, Chips, Field } from "../components/Common";
import { PageFade } from "../components/Motion";

const TYPES = ["Investor inquiry", "Property submission", "Property management", "General inquiry"];

export default function Contact() {
  useSeo("Contact Sido Holdings", "Contact Abdirahman Sido, licensed Oregon Realtor, at Sido Holdings about Oregon commercial real estate acquisitions.");
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", phone: "", inquiry_type: params.get("type") || "Investor inquiry", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageFade>
      <PageHero eyebrow="Contact" title="Let's talk about what you're looking for." image={IMAGES.hero} />
      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x grid gap-14 lg:grid-cols-[1fr_1.5fr]">
          <div data-testid="contact-details">
            <p className="font-serif text-4xl text-navy">Sido Holdings</p>
            <p className="mt-6 text-xl font-semibold text-navy">Abdirahman Sido</p>
            <p className="text-mist">Licensed Oregon Realtor</p>
            <div className="mt-8 space-y-4">
              <a href={PHONE_HREF} className="flex items-center gap-3 text-lg text-navy hover:text-gold" data-testid="contact-phone"><Phone className="h-5 w-5 text-gold" />{PHONE}</a>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-lg text-navy hover:text-gold break-all" data-testid="contact-email"><Mail className="h-5 w-5 text-gold" />{EMAIL}</a>
            </div>
            <div className="mt-10 flex flex-col gap-3 max-w-sm">
              <Link to="/investors#criteria" className="btn-primary" data-testid="contact-cta-criteria">Tell Us What You're Looking For</Link>
              <Link to="/sell" className="btn-ghost text-navy hover:bg-navy/5" data-testid="contact-cta-sell">Submit a Property</Link>
            </div>
          </div>
          {done ? (
            <div className="bg-navy text-paper p-10" data-testid="contact-success">
              <CheckCircle2 className="h-10 w-10 text-gold" strokeWidth={1.5} />
              <p className="mt-6 font-serif text-3xl">Thank you. Sido Holdings will be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-navy/10 p-5 sm:p-10 space-y-5" data-testid="contact-form">
              <div><span className="label">Inquiry type</span><Chips multi={false} options={TYPES} value={f.inquiry_type} onChange={(v) => setF({ ...f, inquiry_type: v })} testid="contact-type" /></div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Name *"><input required className="field" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" /></Field>
                <Field label="Phone"><input type="tel" className="field" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="contact-phone-input" /></Field>
              </div>
              <Field label="Email *"><input required type="email" className="field" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email-input" /></Field>
              <Field label="Message *"><textarea required className="field min-h-[150px]" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" /></Field>
              <button disabled={busy} className="btn-primary w-full sm:w-auto !px-10" data-testid="contact-submit">{busy ? "Sending…" : "Send Message"}</button>
            </form>
          )}
        </div>
      </section>
    </PageFade>
  );
}
